import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { useApi } from '@/hooks/use-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface RouteStat {
  route: string;
  requests: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  errors5xx: number;
}
interface SlowQueryStat {
  service: string;
  calls: number;
  p95Ms: number;
  maxMs: number;
}
interface DailyVolume {
  day: string;
  requests: number;
  errors4xx: number;
  errors5xx: number;
}

const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '12px',
} as const;

const fmtMs = (ms: number) => (ms >= 10_000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

export function ApiMetricsPage() {
  const routes = useApi<RouteStat[]>('/api/api-metrics/routes');
  const slowQueries = useApi<SlowQueryStat[]>('/api/api-metrics/slow-queries');
  const daily = useApi<DailyVolume[]>('/api/api-metrics/daily');

  const all = [routes, slowQueries, daily];
  const anyLoading = all.some((a) => a.loading);
  const errors = all.map((a) => a.error).filter((e): e is string => e !== null);
  const lastFetched = all
    .map((a) => a.lastFetched)
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const refreshAll = () => {
    fetch('/api/cache/invalidate', { method: 'POST' })
      .catch(() => {})
      .finally(() => all.forEach((a) => a.refresh()));
  };

  return (
    <div className="flex flex-col">
      <Header
        title="API"
        lastFetched={lastFetched ?? null}
        onRefresh={refreshAll}
        loading={anyLoading}
      />
      <div className="space-y-6 p-6">
        {errors.length > 0 && <ErrorState message={errors.join(' · ')} onRetry={refreshAll} />}
        {anyLoading && !routes.data && <LoadingState rows={4} />}
        {daily.data && <DailyVolumeChart days={daily.data} />}
        {routes.data && <RoutesTable rows={routes.data} />}
        {slowQueries.data && <SlowQueriesTable rows={slowQueries.data} />}
      </div>
    </div>
  );
}

function DailyVolumeChart({ days }: { days: DailyVolume[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Requests per day (14d)</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={days}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="day" fontSize={11} tickFormatter={(d: string) => d.slice(5)} />
            <YAxis fontSize={11} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Line type="monotone" dataKey="requests" stroke="var(--chart-1)" dot={false} />
            <Line type="monotone" dataKey="errors5xx" stroke="var(--destructive)" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function RoutesTable({ rows }: { rows: RouteStat[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Route latency, slowest p95 first (7d)</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Route</TableHead>
              <TableHead className="text-right">Requests</TableHead>
              <TableHead className="text-right">p50</TableHead>
              <TableHead className="text-right">p95</TableHead>
              <TableHead className="text-right">Max</TableHead>
              <TableHead className="text-right">5xx</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.route}>
                <TableCell className="font-mono text-xs">{r.route}</TableCell>
                <TableCell className="text-right">{r.requests}</TableCell>
                <TableCell className="text-right">{fmtMs(r.p50Ms)}</TableCell>
                <TableCell className="text-right">{fmtMs(r.p95Ms)}</TableCell>
                <TableCell className="text-right">{fmtMs(r.maxMs)}</TableCell>
                <TableCell className={`text-right ${r.errors5xx > 0 ? 'text-destructive' : ''}`}>
                  {r.errors5xx}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SlowQueriesTable({ rows }: { rows: SlowQueryStat[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Slowest DB query services (7d, from [Query] traces)</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service.method</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">p95</TableHead>
              <TableHead className="text-right">Max</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.service}>
                <TableCell className="font-mono text-xs">{r.service}</TableCell>
                <TableCell className="text-right">{r.calls}</TableCell>
                <TableCell className="text-right">{fmtMs(r.p95Ms)}</TableCell>
                <TableCell className="text-right">{fmtMs(r.maxMs)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
